import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, CircularProgress, IconButton, Link, Typography, useEventCallback } from '@mui/material';
import {
  AccountCircle,
  ArrowBack as ChevronLeftIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Textsms,
  Update,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Prompt, Router } from 'react-router-dom';

import { FilterExpression, NumberFilter } from '@playq/octopus-common';
import {
  AppEntitiesResponse,
  AppEntityFeatures,
  AppEntityFilterField,
  AppEntityVersionResponse,
} from '@playq/octopus2-apps';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';
import { Either } from '@playq/irt';

import { ThemeModeContext, ThemeProvider } from '/common';
import { IErrorBoundaryQueryData, IThemeModeContext, QueryFilter } from '/common/models';
import { confirmAboutUnsavedChanges, formatDate, getErrorBoundaryQueryKey } from '/helpers';
import { useMounted } from '/hooks';
import { IBasePromotionService } from '/api/hooks/promotions';
import { IVersionPickerProps, useVersionPicker } from '/shared/AppEntityVersionPicker';
import { ControlBarPromotionsPreview, sandboxColor } from '/shared/Promotions';
import { EntityID } from '/shared/Promotions/components/Service/types';
import { LabelThemed, LabelThemedColor } from '/shared/LabelThemed';
import { confirmDialog, ConfirmDialogType } from '/common/ConfirmDialog';
import { iteratorMaxLimit } from '/constants';
import { history } from '/Router/history';

import { IconButtonWithTooltip } from '../IconButtonWithTooltip';

import {
  ContentStyled,
  ControlBarDivider,
  ControlBarInput,
  ControlBarRoot,
  ControlBarWrapper,
  ErrorMessage,
  LogData,
  LogDataInfo,
  LogDataInfoRow,
  pickerButtonHighlightedCSS,
  StickyContainer,
  tooltipCSS,
  versionPickerTheme,
} from './styles';
import { AppEntityControlBarProps, ILogRow } from './types';

export function AppEntityControlBar<ID extends EntityID>({
  componentIdLabels,
  componentState,
  label,
  entityId,
  service,
  disabled = false,
  disabledPromotion = false,
  version,
  content,
  withoutContentDivider,
  manifestName,
  notificationLabel,
  withPromotion = false,
  canSearch,
  canSave,
  disabledSave = false,
  canReset,
  saving: propSaving,
  processing,
  spaceless,
  canWrite,
  canWriteLive,
  isVersioned = true,
  promotionResolverData,
  backURL,
  wasLive = false,
  isCreating = false,
  refreshData,
  openPromotionDialog,
  isCloned,
  noChanges: propsNoChanges,
  versionPickerProps,
  className,
  versionCommentComponent,
  validate,
  onSearch,
  onEntitySelect,
  onPromotionsChange,
  afterModalOpen,
  onOpenPromotionDialogAfterUpdating,
  onSave,
  onReset,
  onGoBack,
  onUnsavedChangesPrompt,
  onSaveLabel,
  styles = {},
}: AppEntityControlBarProps<ID>) {
  const { currentThemeMode }: IThemeModeContext = useContext(ThemeModeContext);
  const saving = !!propSaving;

  const { content: contentCSS } = styles;
  const noChanges = propsNoChanges === true && !isCloned;

  const [versionPickerNode, pickVersion] = useVersionPicker<ID>();
  const isMounted = useMounted();
  const [filter, setFilter] = useState('');
  const [currentVersion, setCurrentVersion] = useState<AppEntityVersionResponse | null>(null);
  const [isPromotedOnLive, setIsPromotedOnLive] = useState<boolean>(false);
  const [highlightVersionPicker, setHighlightVersionPicker] = useState<boolean>(false);
  const [shouldOpenPromotions, setShouldOpenPromotions] = useState<boolean>(openPromotionDialog ?? false);
  const [lastVersion, setLastVersion] = useState<number>();
  const [hasErrors, setHasErrors] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const errorBoundaryQueryKey = getErrorBoundaryQueryKey(history.location.pathname);

  const queryVersions = useMemo(() => service?.queryVersions?.bind(service), [service]);

  const getVersion = useCallback(
    (nVersion?: number) => {
      if (!entityId || !service?.queryVersions) {
        return;
      }

      const iterator = new OffsetLimit({ limit: nVersion === undefined ? iteratorMaxLimit : 1, offset: 0 });
      const filterBy: QueryFilter =
        nVersion === undefined
          ? {}
          : {
              [AppEntityFilterField.Version]: new NumberFilter({
                left: nVersion,
                right: undefined,
                expression: FilterExpression.Equal,
              }),
            };

      service
        .queryVersions(entityId, iterator, [], filterBy, [AppEntityFeatures.AuthorName])
        .then((data: Either<GenericFailure, AppEntitiesResponse>) => {
          if (isMounted.current) {
            data.bifold(
              (res) => {
                if (nVersion !== undefined) {
                  setCurrentVersion(res.versions[0]);
                  return;
                }
                setLastVersion(res.versions[0]?.version);
              },
              (err) => console.error(err)
            );
          }
        });
    },
    [entityId, service, isMounted]
  );

  const enableClearErrorBoundaryQueryData = useEventCallback(() => {
    queryClient.setQueryData<IErrorBoundaryQueryData>(errorBoundaryQueryKey, (prevState) => ({
      ...prevState,
      disableClear: false,
    }));
  });

  useEffect(() => {
    enableClearErrorBoundaryQueryData();
  }, [enableClearErrorBoundaryQueryData]);

  useEffect(() => {
    getVersion(version);
    // need to listen version only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const selectVersion = async () => {
    const selectedVersion = await pickVersion({
      entityId,
      version,
      queryVersions,
      queryPromotions: service?.queryPromotions?.bind(service),
      ...versionPickerProps,
    } as IVersionPickerProps<ID>);

    if (selectedVersion) {
      onEntitySelect?.(selectedVersion);
    }
  };

  const handleVersionSelect = () => {
    if (entityId && version !== undefined && queryVersions && onEntitySelect) {
      confirmAboutUnsavedChanges(selectVersion, noChanges);
    }
  };

  const handleVersionPickerOver = () => {
    if (version !== undefined) {
      setHighlightVersionPicker(true);
    }
  };

  const handleVersionPickerLeave = () => {
    setHighlightVersionPicker(false);
  };

  const onSaveClick = () => {
    if (!onSave) {
      return;
    }

    queryClient.setQueryData<IErrorBoundaryQueryData>(errorBoundaryQueryKey, (prevState) => ({
      ...prevState,
      disableClose: false,
      state: componentState ?? prevState?.state,
      disableClear: true,
    }));

    if (validate !== undefined) {
      const newHasErrors = validate();
      setHasErrors(newHasErrors);
      if (newHasErrors) {
        return;
      }
    }

    if (!isPromotedOnLive) {
      onSave();
      return;
    }

    confirmDialog({
      title: 'WARNING',
      content:
        'Saving will not automatically promote the new version to Live. If you want to promote the new version, stay on the page and promote it manually',
      type: ConfirmDialogType.Warning,
      closeButton: { label: 'Save' },
      onClose: () => {
        onSave();
        setShouldOpenPromotions(false);
      },
      successButton: { label: 'Save and open promotions editor' },
      onSuccess: () => {
        onSave();
        setShouldOpenPromotions(true);
        onOpenPromotionDialogAfterUpdating?.(true);
      },
    });
  };

  const onResetClick = () => {
    setHasErrors(false);

    queryClient.setQueryData<IErrorBoundaryQueryData>(errorBoundaryQueryKey, (prevState) => ({
      ...prevState,
      state: undefined,
      disableClear: false,
    }));

    if (onReset) {
      onReset();
    }
  };

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFilter(value);

    if (onSearch) {
      onSearch(value);
    }
  };

  const onSearchClear = () => {
    setFilter('');

    if (onSearch) {
      onSearch('');
    }
  };

  // it should listen only saving to detect when it is finished and then open promotions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shouldOpenPromotionsAfterSave = useMemo(() => shouldOpenPromotions && !saving, [saving]);

  const promotionVersion = useMemo(() => {
    if (!propSaving && shouldOpenPromotions && lastVersion !== undefined && lastVersion > (version ?? 0)) {
      return lastVersion + 1;
    }
    return version;
  }, [shouldOpenPromotions, propSaving, lastVersion, version]);

  const handleClosePromotionsDialog = useCallback(() => {
    setShouldOpenPromotions(false);
  }, []);

  function LogRow({ title, children }: ILogRow) {
    return (
      <Typography noWrap={true} component='div' sx={{ lineHeight: 1.4 }}>
        <LogDataInfoRow title={title}>{children}</LogDataInfoRow>
      </Typography>
    );
  }

  const hasErrorsIcon = useMemo(() => {
    if (hasErrors) {
      return <WarningIcon htmlColor={sandboxColor} />;
    }
  }, [hasErrors]);

  return (
    <Router history={history}>
      <Prompt
        when={propsNoChanges === false}
        message={onUnsavedChangesPrompt ?? 'Are you sure? Unsaved changes will be lost.'}
      />
      <StickyContainer className={className} data-testid='control-bar'>
        <ControlBarRoot elevation={1}>
          <ControlBarWrapper>
            {onGoBack && (
              <Link href={backURL}>
                <IconButton onClick={onGoBack} data-testid='go-back-button' size='large'>
                  <ChevronLeftIcon />
                </IconButton>
              </Link>
            )}

            {typeof label === 'string' ? (
              <Typography
                mr={1}
                variant='h5'
                color={ThemeProvider.getAppropriateStyles(currentThemeMode, 'primary', 'text.primary')}
              >
                {label}
              </Typography>
            ) : (
              label
            )}

            <Box data-testid='id-span' sx={{ display: 'flex' }}>
              {componentIdLabels}
            </Box>

            {isVersioned && !isCloned && (
              <LabelThemed
                onClick={handleVersionSelect}
                onMouseOver={handleVersionPickerOver}
                onMouseLeave={handleVersionPickerLeave}
                color={version !== undefined ? LabelThemedColor.Primary : LabelThemedColor.Grey}
                clickable={version !== undefined}
                data-testid='select-version-btn'
                theme={versionPickerTheme}
                withoutMargins={{ right: false }}
                css={highlightVersionPicker ? pickerButtonHighlightedCSS : undefined}
              >
                {version !== undefined ? `v. ${version}` : 'Draft'}
                <ArrowDropDownIcon
                  sx={{
                    fontSize: 14,
                    verticalAlign: 'bottom',
                  }}
                />
              </LabelThemed>
            )}

            {refreshData?.shouldRefresh && (
              <IconButtonWithTooltip
                tooltipProps={{
                  title: refreshData.message,
                }}
                icon={<RefreshIcon />}
                disabled={refreshData.isRefreshDisabled}
                tooltipInitialFadeOut={true}
                onClick={refreshData.onRefresh}
                styles={{ tooltip: tooltipCSS }}
              />
            )}

            <LogData>
              {currentVersion && !isCloned && (
                <>
                  <LogDataInfo noComment={!currentVersion.comment}>
                    <LogRow title={currentVersion.authorName}>
                      <AccountCircle sx={{ fontSize: '20px' }} color='secondary' />
                      <span>{currentVersion.authorName}</span>
                    </LogRow>

                    <LogRow title={currentVersion.updatedAt.toDateString()}>
                      <Update sx={{ fontSize: '20px' }} color='primary' />
                      <span>{formatDate(currentVersion.updatedAt)}</span>
                    </LogRow>
                  </LogDataInfo>
                  {currentVersion.comment && (
                    <LogRow title={currentVersion.comment}>
                      <Textsms sx={{ fontSize: 20 }} color='action' />
                      {versionCommentComponent?.(currentVersion.comment) ?? (
                        <Typography noWrap={true} variant='caption'>
                          {currentVersion.comment}
                        </Typography>
                      )}
                    </LogRow>
                  )}
                </>
              )}
            </LogData>

            {canSearch && (
              <>
                <ControlBarInput
                  data-testid='input-filter'
                  disabled={disabled}
                  startAdornment={
                    <IconButton
                      sx={{
                        marginRight: '10px',
                        padding: '10px',
                      }}
                      aria-label='Search'
                      disabled={true}
                      size='large'
                    >
                      <SearchIcon />
                    </IconButton>
                  }
                  endAdornment={
                    filter !== '' && (
                      <IconButton
                        sx={{
                          marginRight: 2,
                          padding: 2,
                        }}
                        aria-label='Clear'
                        onClick={onSearchClear}
                        size='large'
                      >
                        <CloseIcon />
                      </IconButton>
                    )
                  }
                  value={filter}
                  placeholder='Search...'
                  onChange={onSearchChange}
                />

                <ControlBarDivider />
              </>
            )}

            {content !== undefined && (
              <>
                <ContentStyled css={contentCSS}>{content}</ContentStyled>
                {!withoutContentDivider && <ControlBarDivider />}
              </>
            )}

            {withPromotion && version !== undefined && service?.queryPromotions && entityId && (
              <ControlBarPromotionsPreview<ID>
                entityID={entityId}
                service={service as IBasePromotionService<ID>}
                version={promotionVersion}
                lastAvailableVersion={lastVersion}
                promotionResolverData={promotionResolverData}
                disabled={disabled || disabledPromotion}
                spaceless={spaceless}
                notificationLabel={notificationLabel}
                manifestName={manifestName}
                canWrite={canWrite}
                canWriteLive={canWriteLive}
                wasLive={wasLive}
                open={propSaving === undefined ? shouldOpenPromotions : shouldOpenPromotionsAfterSave}
                onChange={onPromotionsChange}
                afterModalOpen={afterModalOpen}
                setIsPromotedOnLive={setIsPromotedOnLive}
                onClose={handleClosePromotionsDialog}
              />
            )}

            {canSave && (
              <div>
                <Button
                  onClick={onSaveClick}
                  startIcon={hasErrorsIcon ?? (saving || processing ? <CircularProgress size={20} /> : <CheckIcon />)}
                  sx={{
                    marginRight: '5px',
                    minWidth: 150,
                  }}
                  disabled={disabled || saving || processing || disabledSave || noChanges}
                  color={isCreating ? 'success' : 'primary'}
                  variant='contained'
                  data-testid='top-save-button'
                >
                  {onSaveLabel ?? (isCreating ? 'Create' : 'Save')}
                </Button>

                {hasErrors && (
                  <ErrorMessage variant='body2' color='error'>
                    Contains errors
                  </ErrorMessage>
                )}
              </div>
            )}

            {canReset && (
              <Button
                sx={{
                  marginRight: '5px',
                  minWidth: 150,
                }}
                disabled={disabled || saving || processing}
                onClick={onResetClick}
                data-testid='button-reset'
              >
                Reset
              </Button>
            )}
          </ControlBarWrapper>
        </ControlBarRoot>
        {versionPickerNode}
      </StickyContainer>
    </Router>
  );
}
